/**
 * Color utility functions for event styling
 */

/**
 * Lighten a hex color by blending it with white
 * Used for completed appointment treatment
 * @param hex - Hex color string (e.g., '#8530F3')
 * @param percent - Percentage to blend towards white (0-100, default 40)
 * @returns Lightened hex color
 * @example
 * lightenColor('#8530F3', 40) // Returns '#c9a7f9'
 */
export const lightenColor = (hex: string, percent: number = 40): string => {
  // Convert hex to RGB
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  // Blend with white based on percent (40% = blend 40% towards white)
  const amount = percent / 100;
  const newR = Math.round(r + (255 - r) * amount);
  const newG = Math.round(g + (255 - g) * amount);
  const newB = Math.round(b + (255 - b) * amount);

  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

// Convert a hex color to an rgba() string at the given alpha — used for the
// "tint on white" backgrounds that replace solid color blocks in the new design.
export const tintColor = (hex: string, alpha: number = 0.06): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Opaque equivalent of `tintColor(hex, alpha)` flattened against white. Use
// this when you want the visual of a soft color tint without the translucency
// artifacts (showing through grid lines, stacking with overlapping events).
export const opaqueTint = (hex: string, alpha: number = 0.06): string =>
  lightenColor(hex, Math.round((1 - alpha) * 100));
